import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Spinner } from "@/components/ui/spinner";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { examples } from "@/content/examples";
import { GitHubIcon } from "@/components/GitHubIcon";
import { repositoryUrl } from "../config";

interface EditorPanelProps {
  onRun: (source: string) => void;
  isRunning: boolean;
}

export function EditorPanel({ onRun, isRunning }: EditorPanelProps) {
  const [selectedTab, setSelectedTab] = useState("editor");
  const [editorText, setEditorText] = useState("");
  const [selectedExampleId, setSelectedExampleId] = useState(
    () => examples[0]?.id ?? "",
  );
  const selectedExample =
    examples.find((example) => example.id === selectedExampleId) ?? examples[0];

  return (
    <section
      aria-label="Workspace"
      className="flex h-full min-h-0 min-w-0 flex-col overflow-hidden"
    >
      <div className="flex min-h-0 flex-1 flex-col p-4">
        <Tabs
          value={selectedTab}
          onValueChange={setSelectedTab}
          className="min-h-0 flex-1 gap-3"
        >
          <div className="flex shrink-0 flex-wrap items-center justify-between gap-3">
            <div className="flex min-w-0 flex-1 flex-wrap items-center gap-3">
              <TabsList
                aria-label="Workspace views"
                className="bg-inset ring-1 ring-divider"
              >
                <TabsTrigger value="editor" className="data-active:bg-page">
                  Editor
                </TabsTrigger>
                <TabsTrigger value="examples" className="data-active:bg-page">
                  Examples
                </TabsTrigger>
              </TabsList>
              {selectedTab === "examples" && examples.length > 0 && (
                <Select
                  value={selectedExampleId}
                  onValueChange={setSelectedExampleId}
                >
                  <SelectTrigger
                    id="example-select"
                    aria-label="Choose an example"
                    className="w-44 shrink-0 bg-background"
                  >
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {examples.map((example) => (
                      <SelectItem key={example.id} value={example.id}>
                        {example.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </div>
            <div className="flex items-center gap-3">
              <Button
                type="button"
                disabled={isRunning}
                aria-busy={isRunning}
                className="bg-green-700 text-white hover:bg-green-800 focus-visible:border-green-700 focus-visible:ring-green-700/40"
                onClick={() =>
                  onRun(
                    selectedTab === "editor"
                      ? editorText
                      : (selectedExample?.text ?? ""),
                  )
                }
              >
                {isRunning && (
                  <Spinner data-icon="inline-start" aria-hidden="true" />
                )}
                {isRunning ? "Running…" : "Run"}
              </Button>
              <Button asChild variant="ghost" size="icon">
                <a
                  href={repositoryUrl}
                  aria-label="View the GitHub repository"
                  title="View the GitHub repository"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <GitHubIcon />
                </a>
              </Button>
            </div>
          </div>
          <TabsContent value="editor" className="flex min-h-0 flex-col gap-2">
            <Textarea
              id="editor-text"
              aria-label="Editor text"
              value={editorText}
              onChange={(event) => setEditorText(event.target.value)}
              placeholder="Start writing here…"
              spellCheck={false}
              className="field-sizing-fixed min-h-0 flex-1 resize-none overflow-auto bg-background font-mono text-sm leading-6"
            />
          </TabsContent>
          <TabsContent value="examples" className="flex min-h-0 flex-col gap-2">
            {selectedExample ? (
              <pre
                role="region"
                aria-label={`${selectedExample.label} (read only)`}
                tabIndex={0}
                className="m-0 min-h-0 flex-1 overflow-auto rounded-lg border bg-background px-2.5 py-2 font-mono text-sm leading-6 whitespace-pre-wrap wrap-break-word outline-none select-none focus-visible:outline-2"
              >
                <code>{selectedExample.text}</code>
              </pre>
            ) : (
              <p className="text-muted-foreground">No examples available.</p>
            )}
          </TabsContent>
        </Tabs>
      </div>
    </section>
  );
}
